import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import { exportBsiTransactionsExcel } from '@/lib/banks/bsi/exportExcel';
import {
  bsiInternetBankApi,
  type BsiIbAccountOption,
  type BsiIbLastThirtyItem,
  type BsiStatementRegistrationSummary,
} from '@/lib/banks/bsi/api';
import BsiTransactionsTable from './BsiTransactionsTable';
import BsiRegisterStatementModal from './BsiRegisterStatementModal';
import { loadBsiStatementRegistrations, updateRegistrationAtIndex } from './statementRegistrations';
import {
  defaultJalaliMonthStart,
  defaultJalaliToday,
  isSessionExpiredMessage,
  type BsiFlashMessage,
} from './helpers';

type Props = {
  sessionValid: boolean;
  preferredAccount?: string;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

const BsiPeriodTab: React.FC<Props> = ({
  sessionValid,
  preferredAccount,
  onMessage,
  onSessionExpired,
}) => {
  const [accounts, setAccounts] = React.useState<BsiIbAccountOption[]>([]);
  const [currencies, setCurrencies] = React.useState<BsiIbAccountOption[]>([]);
  const [accountNumber, setAccountNumber] = React.useState('');
  const [currency, setCurrency] = React.useState('RIAL');
  const [fromDate, setFromDate] = React.useState(defaultJalaliMonthStart);
  const [toDate, setToDate] = React.useState(defaultJalaliToday);
  const [lastDate, setLastDate] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<BsiIbLastThirtyItem[]>([]);
  const [registrations, setRegistrations] = React.useState<BsiStatementRegistrationSummary[]>([]);
  const [loadingForm, setLoadingForm] = React.useState(false);
  const [loadingTx, setLoadingTx] = React.useState(false);
  const [registerIndex, setRegisterIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (preferredAccount) setAccountNumber(preferredAccount);
  }, [preferredAccount]);

  const loadForm = React.useCallback(async () => {
    setLoadingForm(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.dateToDateForm();
      setAccounts(res.data.accounts || []);
      setCurrencies(res.data.currencies || []);
      setLastDate(res.data.last_date || null);
      setAccountNumber((prev) => prev || preferredAccount || res.data.accounts?.[0]?.value || '');
      setCurrency((prev) => prev || res.data.currencies?.[0]?.value || 'RIAL');
      // تاریخ‌ها همیشه بر اساس روز جاری سیستم باشند (نه last_date بانک)
      setToDate(defaultJalaliToday());
      setFromDate(defaultJalaliMonthStart());
    } catch (e) {
      onMessage({ type: 'error', text: e instanceof Error ? e.message : 'خطا در فرم گردش دوره‌ای' });
    } finally {
      setLoadingForm(false);
    }
  }, [onMessage, preferredAccount]);

  React.useEffect(() => {
    if (sessionValid && accounts.length === 0 && !loadingForm) void loadForm();
  }, [sessionValid, accounts.length, loadingForm, loadForm]);

  const handleFetch = async () => {
    if (!accountNumber.trim()) {
      onMessage({ type: 'error', text: 'شماره حساب را انتخاب کنید.' });
      return;
    }
    if (!fromDate || !toDate) {
      onMessage({ type: 'error', text: 'بازه تاریخ را کامل کنید.' });
      return;
    }
    setLoadingTx(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.dateToDate({
        account_number: accountNumber.trim(),
        from_date: fromDate,
        to_date: toDate,
        currency: currency || undefined,
      });
      const nextItems = res.data.items || [];
      setItems(nextItems);
      setLastDate(res.data.last_date || lastDate);
      const regs = await loadBsiStatementRegistrations(accountNumber.trim(), nextItems);
      setRegistrations(regs);
      onMessage({ type: 'success', text: res.message || 'گردش دوره‌ای دریافت شد.' });
    } catch (err) {
      const text = err instanceof Error ? err.message : 'دریافت گردش دوره‌ای ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoadingTx(false);
    }
  };

  const handleExport = async () => {
    if (!items.length) return;
    try {
      await exportBsiTransactionsExcel({
        items,
        accountNumber,
        title: `گردش ${fromDate} تا ${toDate}`,
      });
      onMessage({ type: 'success', text: 'خروجی اکسل آماده شد.' });
    } catch (err) {
      onMessage({ type: 'error', text: err instanceof Error ? err.message : 'خروجی اکسل ناموفق بود.' });
    }
  };

  const registerItem = registerIndex != null ? items[registerIndex] ?? null : null;

  return (
    <>
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="text-base">گردش دوره‌ای (از تاریخ تا تاریخ)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sessionValid ? (
            <p className="text-sm text-amber-700">برای دریافت گردش، ابتدا وارد اینترنت‌بانک شوید.</p>
          ) : (
            <>
              {lastDate && <p className="text-xs text-slate-500">آخرین تاریخ مجاز بانک: {lastDate}</p>}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label>شماره حساب</Label>
                  {accounts.length > 0 ? (
                    <SearchableSelect
                      value={accountNumber}
                      onChange={setAccountNumber}
                      options={accounts.map((a) => ({ value: a.value, label: a.label || a.value }))}
                      placeholder={loadingForm ? 'در حال دریافت…' : 'انتخاب حساب'}
                      disabled={loadingForm}
                    />
                  ) : (
                    <Input
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder="شماره حساب"
                      disabled={loadingForm}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>از تاریخ</Label>
                  <PersianDatePickerFa value={fromDate} onChange={setFromDate} />
                </div>
                <div className="space-y-2">
                  <Label>تا تاریخ</Label>
                  <PersianDatePickerFa value={toDate} onChange={setToDate} />
                </div>
                <div className="space-y-2">
                  <Label>نوع ارز</Label>
                  <SearchableSelect
                    value={currency}
                    onChange={setCurrency}
                    options={
                      currencies.length
                        ? currencies.map((c) => ({ value: c.value, label: c.label || c.value }))
                        : [{ value: 'RIAL', label: 'ریال ایران' }]
                    }
                    placeholder="ارز"
                  />
                </div>
                <div className="flex items-end gap-2 md:col-span-2">
                  <Button type="button" variant="outline" onClick={() => void loadForm()} disabled={loadingForm}>
                    {loadingForm ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    <span className="mr-1">بروزرسانی فرم</span>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void handleFetch()}
                    disabled={loadingTx || !accountNumber.trim()}
                  >
                    {loadingTx ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                    بارگذاری گردش
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {items.length > 0 && (
        <div className="lg:col-span-3">
          <BsiTransactionsTable
            title={`گردش دوره‌ای — ${items.length} ردیف (${fromDate} تا ${toDate})`}
            items={items}
            registrations={registrations}
            onRegister={setRegisterIndex}
            onExport={() => void handleExport()}
          />
        </div>
      )}

      <BsiRegisterStatementModal
        open={registerIndex != null}
        accountNumber={accountNumber.trim()}
        reportSource="date_to_date"
        item={registerItem}
        existing={registerIndex != null ? registrations[registerIndex] : null}
        onClose={() => setRegisterIndex(null)}
        onSaved={(registration) => {
          if (registerIndex == null) return;
          setRegistrations((prev) => updateRegistrationAtIndex(prev, registerIndex, registration));
          onMessage({ type: 'success', text: 'ردیف گردش ثبت شد.' });
        }}
      />
    </>
  );
};

export default BsiPeriodTab;
